/** An intentionally unreadable suspended lozenge for the cull bay. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('cull');
  if (!found) throw new Error('hanging shape: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`hanging shape: cull surface ${String(index)} missing`);
  return colour;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hanging-shape';
  const linkMaterial = surface('metal', zoneSurface(1));
  const bodyMaterial = surface('organic', zoneSurface(2));

  // The radius reaches its maximum about forty percent down from the top and
  // then declines without any local break. A separate head, feet, or even a
  // sharper shoulder would let the backlight resolve this as an animal, which
  // would betray the distance and frost that Beat 5 depends on.
  const body = new THREE.Mesh(new THREE.LatheGeometry([
    new THREE.Vector2(0.11, 0),
    new THREE.Vector2(0.14, 0.05),
    new THREE.Vector2(0.17, 0.15),
    new THREE.Vector2(0.20, 0.35),
    new THREE.Vector2(0.21, 0.60),
    new THREE.Vector2(0.21, 0.69),
    new THREE.Vector2(0.20, 0.80),
    new THREE.Vector2(0.16, 0.99),
    new THREE.Vector2(0.10, 1.11),
    new THREE.Vector2(0.08, 1.15),
  ], 16), bodyMaterial);
  body.name = 'smooth-lozenge-body';
  body.castShadow = true;
  body.receiveShadow = true;

  const link = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.10, 8), linkMaterial);
  link.name = 'short-rail-link';
  link.position.y = 1.20;
  link.castShadow = true;
  group.add(body, link);

  return group;
}

export const hangingShapeAsset: AssetDefinition = {
  id: 'hanging-shape',
  label: 'Hanging lozenge',
  description: 'Smooth, detail-free vertical form for a backlit cull-bay silhouette.',
  footprint: { width: 0.42, height: 1.25, length: 0.42 },
  create: build,
};
